import { Component, Input, OnChanges, SimpleChanges, OnInit, Inject, ChangeDetectionStrategy } from '@angular/core';

import { ENVIRONMENT, Environment } from '@environment';
import { Crew, Movie, MovieCrewRole, MovieEpisode, Star } from '@usheru-hff/shared/domain';

/**
 * Component to display movie data related to people involved.
 *
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - use env values in their own method and subscribe to env changes
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-movie-credits',
  templateUrl: './movie-credits.component.html',
  styleUrls: ['./movie-credits.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MovieCreditsComponent implements OnInit, OnChanges {
  /**
   * @docNotNeccesary
   */
  @Input() movie?: Movie;

  /**
   * @docNotNeccesary
   */
  producingCompanies: string;

  /**
   * @docNotNeccesary
   */
  crewListing: CrewListing[];

  /**
   * Alternative of {@link crewListing}.
   */
  customCrew: unknown[];

  /**
   * Environment option.
   */
  canCollapse = false;

  /**
   * Hide the content and shows only the header.
   */
  collapsed = false;

  /**
   * If {@link movie} has episodes set this value.
   */
  multipleSeasons!: boolean;

  constructor(@Inject(ENVIRONMENT) private env: Environment) {
    this.canCollapse = this.env?.customPageParams?.movieCreditsCollapse;
    this.collapsed = this.canCollapse && this.env?.customPageParams?.movieCreditsCollapseInitial;
  }

  /**
   * @docNotNeccesary
   *
   * //TODO barbeitoalex propostal - remove the ngOnInit. Move this part to ngOnChanges. Also transform the producingCompanies to another his own method.
   */
  ngOnInit() {
    this.setMovieCrew();
    if (this.movie.producingCompanies && this.env.customPageParams?.movieCreditsProducingCompanies) {
      this.producingCompanies = this.movie.producingCompanies.map(p => p.name).join(', ');
    }
  }

  /**
   * @docNotNeccesary
   */
  ngOnChanges(changes: SimpleChanges) {
    if (
      !changes.movie?.firstChange &&
      (changes.movie.currentValue as Movie).friendlyUrl != (changes.movie.previousValue as Movie).friendlyUrl
    )
      this.setMovieCrew();
  }

  /**
   * Inits the {@link crewListing} or {@link customCrew} if the {@link movie} has customMovieCrew.
   */
  private setMovieCrew() {
    // this.addProductionCompaniesIfExists();
    if (this.movie?.customMovieCrew) {
      this.setCustomCredits();
    } else if (this.movie.movieCrew) {
      this.setRegularCredits();
    }
  }

  /**
   * Creates the {@link crewMember} from {@link movie}. Filter the roles availables
   * and tries to get this config from {@link Environment} (movieCrewPipe, movieCrewRoleListing).
   */
  private setRegularCredits() {
    this.crewListing = [];
    let crewMembers: Crew[];
    let roles: MovieCrewRole[] = [];
    let movieCrew = this.movie.movieCrew;

    // Merge Episodes Crew Memebers
    if (this.movie.episodes) {
      const { episodes } = this.movie;
      const seasonExample = episodes[0].seasonNumber;
      this.multipleSeasons = episodes.filter(e => e.seasonNumber != seasonExample).length > 0;

      episodes.forEach(({ episodeCrew, episodeNumber, seasonNumber }) => {
        episodeCrew.map(ep => {
          ep.star.episodes = [{ episodeNumber, seasonNumber }];
          return ep;
        });
        movieCrew = movieCrew.concat(episodeCrew);
      });

      // remove duplicate and merge episodes
      movieCrew = movieCrew.reduce((crewFiltered, crewMemeber) => {
        const { star, crewRole } = crewMemeber;
        const { id: idStar, episodes } = star;
        const { id: idRol } = crewRole;
        const foundCrewMember = crewFiltered.find(cm => cm.star.id === idStar && cm.crewRole.id === idRol);
        if (foundCrewMember) {
          const { episodes: foundCrewMembreEpisodes } = foundCrewMember.star;
          if (foundCrewMembreEpisodes && !episodes) {
            foundCrewMember.star.episodes = episodes;
          } else if (foundCrewMembreEpisodes && episodes) {
            foundCrewMember.star.episodes.concat(foundCrewMembreEpisodes);
          }
        } else {
          crewFiltered.push(crewMemeber);
        }
        return crewFiltered;
      }, [] as Crew[]);

      // sort episodes and generate tooltip text
      movieCrew = movieCrew.map(crewMember => {
        const { star } = crewMember;
        const { episodes } = star;
        if (episodes) {
          episodes.sort((a, b) => {
            const { seasonNumber: seasonNumberA, episodeNumber: episodeNumberA } = a;
            const { seasonNumber: seasonNumberB, episodeNumber: episodeNumberB } = b;
            return seasonNumberA != seasonNumberB ? seasonNumberA - seasonNumberB : episodeNumberA - episodeNumberB;
          });
          star.episodestooltip = this.generateEpisodesTooltip(episodes);
        }
        return crewMember;
      });
    }

    // apply crew pipe if there is any for the channel
    if (this.env.movieCrew?.movieCrewPipe) {
      crewMembers = this.env.movieCrew.movieCrewPipe(movieCrew);

      // filter roles available
      roles = this.getRolesFromCrewMembers(crewMembers);
    } else if (this.env.movieCrew?.movieCrewRoleListing) {
      // for each role add a crewListing
      crewMembers = movieCrew;
      roles = this.env.movieCrew.movieCrewRoleListing;
    } else {
      // there is no roles or filtering whatsoever so display all the roles comming from dashboard
      crewMembers = movieCrew;
      roles = this.getRolesFromCrewMembers(movieCrew);
    }

    roles.forEach(role => {
      this.crewListing.push(this.getCrewListingByRole(role, crewMembers));
    });
  }

  /**
   * Creates the {@link customCrew} from the {@link movie} customMovieCrew.
   */
  private setCustomCredits() {
    try {
      const tmp = JSON.parse(this.movie.customMovieCrew);
      this.customCrew = [];
      for (const i in tmp) {
        this.customCrew.push([i, tmp[i]]);
      }
    } catch (error) {
      console.error('bad custom crew format');
    }
  }

  /**
   * Get each rol with a least one crew member
   */
  private getRolesFromCrewMembers(crew: Crew[]): MovieCrewRole[] {
    const uniqueRoles: MovieCrewRole[] = [];

    crew.forEach(crewMem => {
      const isDuplicated = uniqueRoles.includes(crewMem.crewRole.trMsg);
      if (!isDuplicated) uniqueRoles.push(crewMem.crewRole.trMsg);
    });

    return uniqueRoles;
  }

  /**
   * Gets crew that shared the same by rol.
   */
  private getCrewListingByRole(role: MovieCrewRole, movieCrew: Crew[]): CrewListing {
    const stars: Star[] = [];

    movieCrew
      .filter(crew => crew.crewRole.trMsg === role)
      .sort((a, b) => a.position - b.position)
      .forEach(crew => stars.push(crew.star));

    if (stars.length > 0) {
      return {
        role,
        roleClass: this.getRoleClass(role),
        starList: stars
      };
    } else {
      return null;
    }
  }

  private getRoleClass(role: string) {
    return role.replace('crew.role.', '').replaceAll('.', '-').toLowerCase();
  }

  /**
   * @deprecard Don't found any use
   */
  private toSeparatedNames(stars: Star[]): string[] {
    return stars ? stars.map(p => p.name) : null;
  }

  /**
   * @deprecard Don't found any use
   */
  private toCammelCase(text: string) {
    let newText = text;
    if (text.indexOf(' ') > -1) {
      newText = text.split(' ').join('');
    }
    return newText.charAt(0).toLowerCase() + newText.slice(1);
  }

  /**
   * @docNotNeccesary
   */
  toggleCollapse(value?: boolean) {
    if (this.canCollapse) this.collapsed = value || !this.collapsed ? true : false;

    // if (!this.collapsed) {
    //TODO Tracking - maybe track once por load?
    // }
  }

  /**
   * This is deprecated I think we dont need it.
   */
  // addProductionCompaniesIfExists(): void {
  //   if (this.movie.producingCompanies) {
  //     this.movie.producingCompanies.forEach(company => {
  //       const crewCompany = {
  //         crewRole: {
  //           name: 'crew.role.production-company'
  //         },
  //         star: {
  //           name: company.name
  //         }
  //       };
  //       this.movie.movieCrew.push(crewCompany);
  //     });
  //   }
  // }

  generateEpisodesTooltip(episodes: MovieEpisode[]): string | null {
    if (episodes) {
      if (this.multipleSeasons) {
        let text = '';
        episodes
          .reduce((map, episode) => {
            const { seasonNumber } = episode;
            const foundSeason = map.get(episode.seasonNumber);
            if (foundSeason) {
              foundSeason.push(episode);
            } else {
              map.set(seasonNumber, [episode]);
            }
            return map;
          }, new Map<number, MovieEpisode[]>())
          .forEach((episodes, season) => {
            text += `Season ${season}: ${episodes.map(e => e.episodeNumber).join(', ')} | `;
          });
        text = text.slice(0, -3);
        return text;
      } else {
        return `Episodes: ${episodes.map(e => e.episodeNumber).join(', ')}`;
      }
    } else {
      return null;
    }
  }
}
interface CrewListing {
  role: MovieCrewRole;
  roleClass?: string;
  starList: Star[];
}
